import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import NoData from '../../components/common/NoData';
import GraphLoader from '../../components/loaders/GraphLoader';
import { graphColors } from '../../utils/constant';
import { convertToHoursAndMinutes, convertToMinutes } from '../../utils/common_functions';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const total = payload.reduce((sum, entry) => {
      const totalMins = entry?.value?.split('.');
      return sum + convertToMinutes(totalMins[0], totalMins[1]);
    }, 0);

    let { hours, minutes } = convertToHoursAndMinutes(total);
    return (
      <div style={{ backgroundColor: '#fff', border: '1px solid #ccc', padding: 10 }}>
        <strong>{label}</strong>
        <ul style={{ paddingLeft: 10, margin: '6px 0' }}>
          {payload.map((entry) => (
            <li key={entry.name} style={{ color: entry.color, textAlign: 'left' }}>
              {entry.name}: {entry.value}hrs
            </li>
          ))}
        </ul>
        <div>
          <strong>Total:</strong> {hours ? hours + ' hours' : ''} {minutes} mins
        </div>
      </div>
    );
  }

  return null;
};

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  label: PropTypes.string
};

const ProjectStackedBarChart = ({ loading, apiResponse }) => {
  const [hiddenProjects, setHiddenProjects] = useState([]);

  const chartData = useMemo(() => {
    return apiResponse?.map((item) => {
      const row = { name: item.label };
      item.projects
        .filter((project) => project.project_id && project.project_name)
        .forEach((project) => {
          let { hours, minutes } = convertToHoursAndMinutes(project?.total_mins);
          row[project.project_name] = `${hours || 0}.${minutes || 0}`;
        });
      return row;
    });
  }, [apiResponse]);

  const projectNames = useMemo(() => {
    const all =
      apiResponse?.flatMap((item) =>
        item.projects?.filter((p) => p.project_id && p.project_name).map((p) => p.project_name)
      ) || [];

    const result = [...new Set(all)];
    setHiddenProjects(result);
    return result;
  }, [apiResponse]);

  const handleLegendClick = useCallback(
    (data) => {
      const { value } = data;
      setHiddenProjects((prev) =>
        prev.includes(value) ? prev.filter((p) => p !== value) : [...prev, value]
      );
    },
    [setHiddenProjects]
  );

  return loading ? (
    <GraphLoader type="bar" />
  ) : !loading && chartData?.length > 0 ? (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={chartData}>
        <CartesianGrid strokeDasharray="1" vertical={false} horizontal={true} />
        <XAxis dataKey="name" axisLine={false} tickLine={false} />
        <YAxis domain={['auto', 'auto']} axisLine={false} tickLine={false} />
        <Tooltip content={<CustomTooltip />} />
        <Legend onClick={handleLegendClick} />
        {projectNames?.map((project, index) => (
          <Bar
            key={project}
            dataKey={project}
            stackId="a"
            fill={graphColors[index % graphColors.length]}
            name={project}
            hide={!hiddenProjects?.includes(project)}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  ) : (
    <NoData title="No Data" subTitle="No project billing found" height={'270px'} />
  );
};

export default ProjectStackedBarChart;

ProjectStackedBarChart.propTypes = {
  apiResponse: PropTypes.array,
  loading: PropTypes.bool
};
